 
import { Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { ConfigListItem, ConfigDetail } from "../../interface/configs";
import { FormConfig } from "../../components/organisms/TaxReturnWebforms/types/formConfig";
import execute from "../http/execute";
import failure from "../http/failure";
import { get, post, put } from "../http/request";
import success from "../http/success";

 
interface ConfigsState {
    configs: ConfigListItem[];
    selectedConfig: ConfigDetail | null;
    extractionConfigs: Record<string, FormConfig>; // Cache: "1040-2024" → FormConfig
    extractionConfigsLoading: Record<string, boolean>; // Loading state per config
    extractionConfigsError: Record<string, string>; // Error state per config (e.g., "Config not found")
    loading: boolean;
}

const initialState: ConfigsState = {
    configs: [],
    selectedConfig: null,
    extractionConfigs: {},
    extractionConfigsLoading: {},
    extractionConfigsError: {},
    loading: false,
};

// Action Types
export const GET_CONFIGS_REQUEST = 'configs/GET_CONFIGS_REQUEST';
export const GET_CONFIGS_SUCCESS = 'configs/GET_CONFIGS_SUCCESS';
export const GET_CONFIGS_FAILURE = 'configs/GET_CONFIGS_FAILURE';

export const GET_CONFIG_CONTENT_REQUEST = 'configs/GET_CONFIG_CONTENT_REQUEST';
export const GET_CONFIG_CONTENT_SUCCESS = 'configs/GET_CONFIG_CONTENT_SUCCESS';
export const GET_CONFIG_CONTENT_FAILURE = 'configs/GET_CONFIG_CONTENT_FAILURE';

export const UPDATE_CONFIG_REQUEST = 'configs/UPDATE_CONFIG_REQUEST';
export const UPDATE_CONFIG_SUCCESS = 'configs/UPDATE_CONFIG_SUCCESS';
export const UPDATE_CONFIG_FAILURE = 'configs/UPDATE_CONFIG_FAILURE';

export const CREATE_CONFIG_REQUEST = 'configs/CREATE_CONFIG_REQUEST';
export const CREATE_CONFIG_SUCCESS = 'configs/CREATE_CONFIG_SUCCESS';
export const CREATE_CONFIG_FAILURE = 'configs/CREATE_CONFIG_FAILURE';

export const CLEAR_SELECTED_CONFIG = 'configs/CLEAR_SELECTED_CONFIG';

export const GET_EXTRACTION_CONFIG_REQUEST = 'configs/GET_EXTRACTION_CONFIG_REQUEST';
export const GET_EXTRACTION_CONFIG_SUCCESS = 'configs/GET_EXTRACTION_CONFIG_SUCCESS';
export const GET_EXTRACTION_CONFIG_FAILURE = 'configs/GET_EXTRACTION_CONFIG_FAILURE';

type ActionParams =
    | { type: typeof GET_CONFIGS_REQUEST }
    | { type: typeof GET_CONFIGS_SUCCESS; data: { configs: ConfigListItem[]; count: number } }
    | { type: typeof GET_CONFIGS_FAILURE }
    | { type: typeof GET_CONFIG_CONTENT_REQUEST }
    | { type: typeof GET_CONFIG_CONTENT_SUCCESS; data: ConfigDetail }
    | { type: typeof GET_CONFIG_CONTENT_FAILURE }
    | { type: typeof UPDATE_CONFIG_REQUEST }
    | { type: typeof UPDATE_CONFIG_SUCCESS; data: { success: boolean; name: string; versionId: string }; content: FormConfig }
    | { type: typeof UPDATE_CONFIG_FAILURE }
    | { type: typeof CREATE_CONFIG_REQUEST }
    | { type: typeof CREATE_CONFIG_SUCCESS; data: { success: boolean; name: string; versionId: string } }
    | { type: typeof CREATE_CONFIG_FAILURE }
    | { type: typeof CLEAR_SELECTED_CONFIG }
    | { type: typeof GET_EXTRACTION_CONFIG_REQUEST; configName: string }
    | { type: typeof GET_EXTRACTION_CONFIG_SUCCESS; configName: string; data: ConfigDetail }
    | { type: typeof GET_EXTRACTION_CONFIG_FAILURE; configName: string; error: string };

function reducer(state = initialState, action: ActionParams): ConfigsState {
    switch (action.type) {
        case GET_CONFIGS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case GET_CONFIGS_SUCCESS:
            return {
                ...state,
                configs: action.data.configs,
                loading: false,
            };
        case GET_CONFIGS_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case GET_CONFIG_CONTENT_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case GET_CONFIG_CONTENT_SUCCESS:
            return {
                ...state,
                selectedConfig: action.data,
                loading: false,
            };
        case GET_CONFIG_CONTENT_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case UPDATE_CONFIG_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case UPDATE_CONFIG_SUCCESS:
            return {
                ...state,
                loading: false,
                selectedConfig: state.selectedConfig ? {
                    ...state.selectedConfig,
                    versionId: action.data.versionId,
                    content: action.content,
                } : null,
            };
        case UPDATE_CONFIG_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case CREATE_CONFIG_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case CREATE_CONFIG_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case CREATE_CONFIG_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case CLEAR_SELECTED_CONFIG:
            return {
                ...state,
                selectedConfig: null,
            };
        case GET_EXTRACTION_CONFIG_REQUEST:
            return {
                ...state,
                extractionConfigsLoading: {
                    ...state.extractionConfigsLoading,
                    [action.configName]: true,
                },
            };
        case GET_EXTRACTION_CONFIG_SUCCESS:
            return {
                ...state,
                extractionConfigs: {
                    ...state.extractionConfigs,
                    [action.configName]: action.data.content,
                },
                extractionConfigsLoading: {
                    ...state.extractionConfigsLoading,
                    [action.configName]: false,
                },
            };
        case GET_EXTRACTION_CONFIG_FAILURE:
            return {
                ...state,
                extractionConfigsLoading: {
                    ...state.extractionConfigsLoading,
                    [action.configName]: false,
                },
                extractionConfigsError: {
                    ...state.extractionConfigsError,
                    [action.configName]: action.error,
                },
            };
        default:
            return state;
    }
}

// Action Creators
export const getConfigs = (payload: Payload<any>) => (dispatch: AppDispatch) => {
    dispatch({ type: GET_CONFIGS_REQUEST });

    execute(get, '/returns-configs', payload.input, undefined, payload.overrideAuth)
        .then(success(dispatch, GET_CONFIGS_SUCCESS, payload))
        .catch(failure(dispatch, GET_CONFIGS_FAILURE, payload));
};

export const getConfigContent = (payload: Payload<{ configName: string }>) => (dispatch: AppDispatch) => {
    dispatch({ type: GET_CONFIG_CONTENT_REQUEST });

    const configName = encodeURIComponent(payload.input?.configName || '');
    execute(get, `/returns-configs/${configName}`, undefined, undefined, payload.overrideAuth)
        .then(success(dispatch, GET_CONFIG_CONTENT_SUCCESS, payload))
        .catch(failure(dispatch, GET_CONFIG_CONTENT_FAILURE, payload));
};

export const updateConfig = (payload: Payload<{ configName: string; content: FormConfig }>) => (dispatch: AppDispatch) => {
    dispatch({ type: UPDATE_CONFIG_REQUEST });

    const configName = encodeURIComponent(payload.input?.configName || '');
    const content = payload.input?.content;
    execute(put, `/returns-configs/${configName}`, { content }, undefined, payload.overrideAuth)
        .then(success(dispatch, UPDATE_CONFIG_SUCCESS, { ...payload, content }))
        .catch(failure(dispatch, UPDATE_CONFIG_FAILURE, payload));
};

export const createConfig = (payload: Payload<{ name: string; content: FormConfig }>) => (dispatch: AppDispatch) => {
    dispatch({ type: CREATE_CONFIG_REQUEST });

    execute(post, '/returns-configs', payload.input, undefined, payload.overrideAuth)
        .then(success(dispatch, CREATE_CONFIG_SUCCESS, payload))
        .catch(failure(dispatch, CREATE_CONFIG_FAILURE, payload));
};

export const clearSelectedConfig = () => ({ type: CLEAR_SELECTED_CONFIG });

// Get extraction config with caching - used by DataEntry components
export const getExtractionConfig = (
    payload: Payload<{ configName: string }>,
) => (dispatch: AppDispatch, getState: () => { configs: ConfigsState }) => {
    const configName = payload.input?.configName || '';
    const { configs: state } = getState();

    // Check if already cached
    if (state.extractionConfigs[configName]) {
        payload.onSuccess?.(state.extractionConfigs[configName]);
        return;
    }

    // Check if already loading
    if (state.extractionConfigsLoading[configName]) {
        return;
    }

    dispatch({ type: GET_EXTRACTION_CONFIG_REQUEST, configName });

    const encodedName = encodeURIComponent(configName);
    execute(get, `/returns-configs/${encodedName}`, undefined, undefined, payload.overrideAuth)
        .then((response: any) => {
            dispatch({
                type: GET_EXTRACTION_CONFIG_SUCCESS,
                configName,
                data: response.data,
            });
            payload.onSuccess?.(response.data.content);
        })
        .catch((error: any) => {
            const errorMessage = error?.response?.status === 404
                ? `Config "${configName}" not found`
                : error?.message || 'Failed to load config';
            dispatch({ type: GET_EXTRACTION_CONFIG_FAILURE, configName, error: errorMessage });
            payload.onError?.(error);
        });
};

export default reducer;
